import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { adapters } from './filters.adapters';
import { filterConfig } from './filters.schemas';
/**
 * Create the context
 *
 * Used for dynamic context typing
 */
const useCreateFiltersContext = ({ filters, }) => {
    // react router search params
    const [searchParams, setSearchParams] = useSearchParams();
    /**
     * Get a filter by name
     */
    const getFilterByName = (name) => {
        const filter = filters.find((filter) => filter.name === name);
        return filter;
    };
    /**
     * Gets all filters with params=true
     */
    const getFiltersWithParams = () => {
        const _filters = filters.filter((filter) => filter.params);
        return _filters;
    };
    /**
     * Value state
     */
    const [values, _setValues] = useState(filters.reduce((prev, current) => {
        return {
            ...prev,
            [current.name]: current.defaultValue,
        };
    }, {}));
    /**
     * Timeouts
     */
    const timeouts = useRef({});
    const [typemap] = useState(filters.reduce((prev, current) => {
        return {
            ...prev,
            [current.name]: current.type,
        };
    }, {}));
    const commitValue = (name, value) => {
        const filter = getFilterByName(name);
        _setValues((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (filter?.params) {
            handleSearchParams(filter, value);
        }
    };
    /**
     * Setter for individual values
     */
    const setValue = (name, value) => {
        // TODO: replace typemap with a config object, indexed by filter name
        // e.g. filters = { isin: { type: 'text', params: 'true' } }
        const filter = getFilterByName(name);
        // Always clear any previous debounce
        if (timeouts.current[name]) {
            clearTimeout(timeouts.current[name]);
        }
        if (filter?.debounce) {
            timeouts.current[name] = setTimeout(() => {
                commitValue(name, value);
            }, filter?.debounce);
            return;
        }
        commitValue(name, value);
    };
    useEffect(() => {
        return () => {
            Object.values(timeouts.current).forEach(clearTimeout);
        };
    });
    /**
     * Handle setting parameters for a filter
     */
    const handleSearchParams = (filter, value) => {
        if (!filter.params)
            return;
        const serializedEntries = filterConfig[filter.type].serialize(filter.name, value);
        serializedEntries.forEach(([name, value]) => {
            if (!value) {
                searchParams.delete(name);
            }
            else {
                searchParams.set(name, value);
            }
        });
        // update url
        setSearchParams(searchParams, { replace: true });
    };
    /**
     * Fetches state from searchParams and sets it on mount
     */
    const hydrateValuesFromSearchParams = () => {
        const filtersWithParams = getFiltersWithParams();
        for (let i = 0; i < filtersWithParams.length; i++) {
            const filter = filtersWithParams[i];
            let deserialized = filterConfig[filter.type].deserialize(searchParams, filter.name);
            if (deserialized === undefined) {
                // @ts-expect-error
                setValue(filter.name, filter.defaultValue);
            }
            else {
                // @ts-expect-error
                setValue(filter.name, deserialized);
            }
        }
    };
    useEffect(() => {
        hydrateValuesFromSearchParams();
    }, []);
    /**
     * Clears all filters
     */
    const clearAllFilters = () => {
        _setValues((prev) => {
            let _new = { ...prev };
            Object.entries(_new).forEach(([key]) => {
                let name = key;
                const type = typemap[name];
                const clearedValue = filterConfig?.[type]?.getClearedValue?.() ?? undefined;
                _new = {
                    ..._new,
                    [name]: clearedValue,
                };
                const filter = getFilterByName(name);
                if (filter?.params) {
                    handleSearchParams(filter, _new[name]);
                }
            });
            return _new;
        });
    };
    /**
     * Check whether a specific filter is active
     */
    const isActive = (name) => {
        const type = typemap[name];
        // @ts-expect-error
        const hasValue = filterConfig[type].hasValue(values[name]);
        return hasValue;
    };
    /**
     * Check whether any filter is active
     */
    const filtersAreActive = useMemo(() => {
        return filters.some((filter) => {
            const _name = filter.name;
            return isActive(_name);
        });
    }, [values, filters]);
    /**
     * Handler to abstract away props per Filter Type
     */
    const registerFilter = (name) => {
        const filterDef = filters.find((filter) => filter.name === name);
        const filterType = filterDef.type;
        return adapters.shadcn[filterType]({
            value: values[name],
            setValue: (val) => {
                setValue(name, val);
            },
        });
    };
    return {
        values,
        setValue,
        clearAllFilters,
        registerFilter,
        isActive,
        filtersAreActive,
    };
};
const filtersContext = createContext(null);
export const useFilters = () => {
    const ctx = useContext(filtersContext);
    if (!ctx)
        throw new Error('useFilters must be used within FiltersProvider');
    return ctx;
};
export const FiltersProvider = ({ filters, children, }) => {
    const ctx = useCreateFiltersContext({ filters });
    return (_jsx(filtersContext.Provider, { value: ctx, children: typeof children === 'function' ? children(ctx) : children }));
};
